import { Alert, AlertDescription, AlertTitle } from "../../../app/components/ui/alert";
import { Input } from "../../../app/components/ui/input";
import { Label } from "../../../app/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../app/components/ui/select";
import { complianceHint, STORE_TYPE_OPTIONS } from "../config/storeTypeFields";

export type ProfileStepId =
  | "BASIC_INFO"
  | "LOCATION"
  | "CONTACT"
  | "COMPLIANCE"
  | "POS_CONFIGURATION";

type Props = {
  activeStepId: ProfileStepId;
  form: Record<string, string>;
  setForm: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  storeType: string | null;
};

export function ProfileStepFields({ activeStepId, form, setForm, storeType }: Props) {
  const hint = complianceHint(storeType);

  return (
    <>
      {activeStepId === "BASIC_INFO" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="store_type">Store type</Label>
            <Select
              value={form.store_type ?? ""}
              onValueChange={(v) => setForm((f) => ({ ...f, store_type: v }))}
              required
            >
              <SelectTrigger id="store_type" className="min-h-9 w-full">
                <SelectValue placeholder="Select store type" />
              </SelectTrigger>
              <SelectContent>
                {STORE_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="trade_name">Trade name (optional)</Label>
            <Input
              id="trade_name"
              value={form.trade_name ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, trade_name: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              value={form.description ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
        </div>
      ) : null}

      {activeStepId === "LOCATION" ? (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address_line1">Address line 1</Label>
              <Input
                id="address_line1"
                required
                value={form.address_line1 ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, address_line1: e.target.value }))}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address_line2">Address line 2</Label>
              <Input
                id="address_line2"
                value={form.address_line2 ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, address_line2: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                required
                value={form.city ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State / region</Label>
              <Input
                id="state"
                required
                value={form.state ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="postal_code">Postal code</Label>
              <Input
                id="postal_code"
                required
                value={form.postal_code ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                required
                value={form.country ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              />
            </div>
          </div>
        </>
      ) : null}

      {activeStepId === "CONTACT" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              required
              value={form.phone ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="alternate_phone">Alternate phone</Label>
            <Input
              id="alternate_phone"
              value={form.alternate_phone ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, alternate_phone: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="support_email">Support email</Label>
            <Input
              id="support_email"
              type="email"
              value={form.support_email ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, support_email: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              value={form.website ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            />
          </div>
        </div>
      ) : null}

      {activeStepId === "COMPLIANCE" ? (
        <>
          {hint ? (
            <Alert>
              <AlertTitle>Compliance</AlertTitle>
              <AlertDescription>{hint}</AlertDescription>
            </Alert>
          ) : null}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="gst_number">GST number</Label>
              <Input
                id="gst_number"
                value={form.gst_number ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, gst_number: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pan_number">PAN</Label>
              <Input
                id="pan_number"
                value={form.pan_number ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, pan_number: e.target.value }))}
              />
            </div>
          </div>
          {storeType === "PHARMACY" ? (
            <div className="space-y-2">
              <Label htmlFor="drug_license_number">Drug license number</Label>
              <Input
                id="drug_license_number"
                required
                value={form.drug_license_number ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, drug_license_number: e.target.value }))}
              />
            </div>
          ) : null}
          {storeType === "LIQUOR" ? (
            <div className="space-y-2">
              <Label htmlFor="excise_license_number">Excise license number</Label>
              <Input
                id="excise_license_number"
                required
                value={form.excise_license_number ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, excise_license_number: e.target.value }))}
              />
            </div>
          ) : null}
          {(storeType === "RESTAURANT" || storeType === "CAFE") && (
            <div className="space-y-2">
              <Label htmlFor="fssai_license_number">FSSAI license number</Label>
              <Input
                id="fssai_license_number"
                required
                value={form.fssai_license_number ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, fssai_license_number: e.target.value }))}
              />
            </div>
          )}
        </>
      ) : null}

      {activeStepId === "POS_CONFIGURATION" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="default_currency">Default currency (ISO code)</Label>
            <Input
              id="default_currency"
              required
              placeholder="INR"
              value={form.default_currency ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, default_currency: e.target.value }))}
            />
          </div>
          <div className="flex min-h-9 items-center gap-2 md:mt-6">
            <input
              id="tax_inclusive_pricing"
              type="checkbox"
              className="size-4"
              checked={form.tax_inclusive_pricing === "true" || form.tax_inclusive_pricing === "on"}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  tax_inclusive_pricing: e.target.checked ? "true" : "false",
                }))
              }
            />
            <Label htmlFor="tax_inclusive_pricing">Tax-inclusive pricing</Label>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="receipt_footer">Receipt footer (optional)</Label>
            <Input
              id="receipt_footer"
              value={form.receipt_footer ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, receipt_footer: e.target.value }))}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
